import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BellRing, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatMoney } from "@/lib/pricing";
import { whatsappUrl } from "@/lib/share";
import { fmtDate } from "@/lib/quoteText";
import { BACKEND_URL } from "@/lib/backendUrl";

const API = `${BACKEND_URL}/api`;
const daysSince = (iso) => Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);

function reminderText(qt, shop, sym) {
  return `Hi ${qt.customer_name || ""}, this is ${shop.name}. Just a friendly reminder on your approved quote ${qt.quote_no}${qt.vehicle ? ` for the ${qt.vehicle}` : ""}${qt.plate ? ` (${qt.plate})` : ""} — ${formatMoney(sym, qt.total)} outstanding. Let us know when it suits you to settle. Thank you! 谢谢`;
}

function FollowupRow({ qt, shop, sym, onPaid }) {
  const days = daysSince(qt.status_changed_at || qt.created_at);
  const chase = () => {
    window.open(whatsappUrl(qt.customer_phone, shop.countryCode, reminderText(qt, shop, sym)), "_blank", "noopener");
    toast.success("Opening WhatsApp reminder");
  };
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-amber-500/20 bg-slate-950/40 px-4 py-3" data-testid={`followup-row-${qt.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-heading font-bold truncate">{qt.customer_name || "Walk-in"}</span>
          <span className="font-mono-num text-xs text-amber-300/90">{qt.quote_no}</span>
          <span className="rounded-full bg-rose-500/15 border border-rose-500/40 text-rose-300 text-[10px] font-bold px-2 py-0.5" data-testid={`followup-days-${qt.id}`}>{days}d unpaid</span>
        </div>
        <div className="text-xs text-slate-400 mt-0.5 truncate">{[qt.plate, qt.vehicle].filter(Boolean).join(" · ")}{qt.customer_phone ? ` · ${qt.customer_phone}` : ""} · approved {fmtDate(qt.status_changed_at || qt.created_at)}</div>
      </div>
      <div className="font-mono-num font-black text-amber-400 text-lg sm:text-right">{formatMoney(sym, qt.total)}</div>
      <div className="flex gap-2 shrink-0">
        <Button size="sm" onClick={chase} disabled={!qt.customer_phone} data-testid={`followup-whatsapp-${qt.id}`} className="bg-emerald-600 hover:bg-emerald-500 text-white h-8">WhatsApp</Button>
        <Button size="sm" variant="outline" onClick={() => onPaid(qt.id)} data-testid={`followup-paid-${qt.id}`} className="h-8 border-slate-700 bg-slate-900/60 text-slate-200 hover:bg-slate-800"><Check className="w-3.5 h-3.5 mr-1" /> Paid</Button>
      </div>
    </div>
  );
}

export default function FollowupReminders({ shop, sym, onChange }) {
  const [items, setItems] = useState([]);
  const load = () => axios.get(`${API}/quotes/followups`).then(({ data }) => setItems(data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const markPaid = async (id) => {
    await axios.patch(`${API}/quotes/${id}/status`, { status: "paid" });
    toast.success("Marked as paid 已付款");
    setItems((l) => l.filter((x) => x.id !== id));
    onChange?.();
  };

  if (!items.length) return null;
  const total = items.reduce((s, x) => s + Number(x.total || 0), 0);
  return (
    <section className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4 sm:p-5 space-y-3" data-testid="followup-reminders">
      <div className="flex items-center gap-2 flex-wrap">
        <BellRing className="w-4 h-4 text-amber-400" />
        <h3 className="font-heading font-bold">Payment follow-ups <span className="font-zh text-amber-300/70 text-sm">待收款提醒</span></h3>
        <span className="text-xs text-slate-400 ml-auto" data-testid="followup-summary">{items.length} approved over 7 days · <span className="font-mono-num text-amber-300">{formatMoney(sym, total)}</span> outstanding</span>
      </div>
      <div className="space-y-2">
        {items.map((qt) => <FollowupRow key={qt.id} qt={qt} shop={shop} sym={sym} onPaid={markPaid} />)}
      </div>
    </section>
  );
}
